import type { ClientWorkspaceProfile } from "@closer/shared";
import { clientWorkspaceProfileUpdateSchema } from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Modal,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { ContractIcon } from "../../components/icons";
import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";
import { WorkspaceServiceRequests } from "./WorkspaceServiceRequests";

type WorkspaceContract = {
  id: string;
  title: string;
  status: string;
  updatedAt: string;
};

/**
 * Müvekkil Alanı yüzeyi (CLOS-629 — `[123]`'ün arayüz yarısı).
 *
 * Alanın kendi profili ve müvekkilin erişebildiği kanonik sözleşmeler. Sponsorun hukuk
 * bürosu bilgisi YALNIZ AD olarak görünüyor: kart, sponsorun *"yönetim veya veri erişimi
 * varmış gibi sunulmasını"* yasaklıyor — burada "bu alanın masrafını kim karşılıyor"
 * sorusunun cevabından fazlası yok.
 *
 * ⚠ Etkin modüller ve görünür limitler SUNUCUDAN geliyor (`config/clientWorkspace.json`).
 * Bileşen hangi modülün açık olduğunu bilmiyor: bir modülün açılması kod dalı değil, sürümlü
 * bir konfigürasyon değişikliği.
 */
export function ClientWorkspacePage() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  /** Ad düzenleme penceresi (CLOS-1117 · yetim uç F). */
  const [adDuzenleAcik, setAdDuzenleAcik] = useState(false);

  const profilAnahtari = tenantKey(tenantId, "workspace", "profil");
  const profil = useQuery({
    queryKey: profilAnahtari,
    queryFn: async () =>
      (await api<ClientWorkspaceProfile>("/api/workspace/profile", { tenantId })).data,
  });

  const sozlesmeler = useQuery({
    queryKey: tenantKey(tenantId, "workspace", "sozlesmeler"),
    queryFn: async () =>
      (await api<WorkspaceContract[]>("/api/workspace/contracts", { tenantId })).data,
  });

  /**
   * ⚠ Profil hatası ekranın TAMAMINI kapatıyor. Kartın kabul kriteri: *"erişim veya alan
   * durumu kaybedildiğinde açık ekran ... güvenli biçimde kapanır."* Hata yalnız bir bölümde
   * gösterilseydi, altındaki sözleşme listesi ekranda kalır ve kullanıcı erişimi sürüyormuş
   * gibi görürdü.
   */
  if (profil.isError) {
    return (
      <div className="flex flex-col gap-4">
        <StateView
          /**
           * ⚠ `existenceIsSecret: false`: bu bir KAYIT değil, kullanıcının zaten içinde
           * olduğu çalışma alanı. Varsayılan dal "Kayıt bulunamadı" derdi ve kullanıcı
           * silinmiş bir kayıt aradığını sanırdı. Hangi yetkinin eksik olduğu yine
           * söylenmiyor — o ayrıntı yetki haritası çıkarır.
           */
          state={resolveErrorState(profil.error, { idempotent: true, existenceIsSecret: false })}
          onRetry={() => void profil.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {profil.isPending ? (
        <div role="status" aria-label="Alan profili yükleniyor">
          <Skeleton lines={3} />
        </div>
      ) : (
        <Card>
          <div className="flex flex-col gap-4">
            {/**
             * ⚠ Başlık KART İÇİNDE kalıyor: alan adı sunucudan geliyor ve yüklenene kadar
             * ekranda başlık yok. Karttan çıkarıp sayfanın tepesine almak, yükleme
             * sırasında boş bir başlık satırı bırakırdı.
             */}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold text-fg-heading sm:text-3xl">
                  {profil.data?.name}
                </h1>
                {/*
                  ⚠ AD DÜZENLEME (CLOS-1117 · yetim uç F). `PATCH /api/workspace/profile`
                  sunucuda vardı, ekranı yoktu. Kapı ucun kendi anahtarı `organization.profile`
                  (CLOS-888 dersi: ekran ucun anahtarını sorar); `organization.view` ile
                  yalnız okuyan temsilciye düğme çizilmiyor.
                */}
                {can("organization.profile") ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setAdDuzenleAcik(true)}
                  >
                    Adı düzenle
                  </Button>
                ) : null}
              </div>
              {/*
                Hukuk Bürosu YALNIZ AD: yönetim bağlantısı ya da büro verisi sunulmuyor.
                Etiket "Sponsor" DEĞİL (CLOS-1123 / Halit S6): sponsor teknik/domain kavramı;
                kullanıcı bu alanı kuran tarafı "hukuk bürom" diye tanır.
              */}
              <p className="rounded-lg bg-primary-soft px-3 py-1.5 text-sm text-primary">
                {`Hukuk Bürosu: ${profil.data?.sponsorName ?? ""}`}
              </p>
            </div>

            {(profil.data?.modules.length ?? 0) > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {(profil.data?.modules ?? []).map((m) => (
                  <Badge key={m.key} tone="neutral">
                    {m.label}
                  </Badge>
                ))}
              </div>
            ) : null}

            {/*
              ⚠ Limit bölümü YALNIZ dolu olduğunda çiziliyor. Sponsorun sayısal hakları
              sponsorun ticari verisi ve varsayılan olarak boş geliyor; boş bir bölüm
              "sayılar var ama yüklenemedi" izlenimi verirdi.
            */}
            {(profil.data?.limits.length ?? 0) > 0 ? (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-fg-strong">Kullanım sınırları</p>
                {(profil.data?.limits ?? []).map((l) => (
                  <p key={l.key} className="text-sm text-fg-muted">
                    {`${l.key}: ${l.value === "unlimited" ? "Sınırsız" : String(l.value)}`}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        </Card>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-fg-heading">Sözleşmelerim</h2>

        {sozlesmeler.isError ? (
          <StateView
            state={resolveErrorState(sozlesmeler.error, { idempotent: true })}
            onRetry={() => void sozlesmeler.refetch()}
          />
        ) : sozlesmeler.isPending ? (
          <div role="status" aria-label="Sözleşmeler yükleniyor">
            <Skeleton lines={3} />
          </div>
        ) : (sozlesmeler.data?.length ?? 0) === 0 ? (
          <EmptyState
            icon={<ContractIcon />}
            title="Henüz paylaşılmış sözleşme yok"
            description="Hukuk büronuz sizinle bir sözleşme paylaştığında burada görünecek."
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {(sozlesmeler.data ?? []).map((s) => (
              <li key={s.id}>
                <Card>
                  <div className="flex flex-wrap items-center gap-2">
                    {/*
                      ⚠ PAYLAŞILAN GÖRÜNÜM, SAHİBİN ROTASI DEĞİL — ölçüldü (4 Eylül 2026).
                      Bağlantı `/app/sozlesmeler/:id`'ye gidiyordu ve temsilci tıklayınca
                      "Kayıt bulunamadı" alıyordu. O rota SAHİBİN bağlamına ait; temsilcinin
                      aktif bağlamı müvekkil alanı, sözleşme ise büroda duruyor. Ürün doğru
                      davranıp 404 veriyordu; yanlış olan bağlantının hedefiydi. Alıcı
                      yüzeyi (`SharedContractDetailPage`) zaten vardı ve tam bu iş için.
                    */}
                    <Link
                      className="mr-auto font-medium text-primary hover:underline"
                      to={`/app/sozlesmeler/paylasilanlar/${s.id}`}
                    >
                      {s.title}
                    </Link>
                    <span className="text-sm text-fg-muted">
                      {new Date(s.updatedAt).toLocaleDateString("tr-TR")}
                    </span>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/*
        ⚠ Hizmet ilişkisi bölümü sözleşmelerden SONRA: kullanıcı önce elindeki işi görüyor,
        sonra "bu büro benim adıma hangi kapsamda çalışıyor" sorusuna bakıyor.
      */}
      <WorkspaceServiceRequests />

      <AlanAdiDialog
        open={adDuzenleAcik}
        mevcutAd={profil.data?.name ?? ""}
        tenantId={tenantId}
        onClose={() => setAdDuzenleAcik(false)}
      />
    </div>
  );
}

/**
 * ALAN ADI DÜZENLEME PENCERESİ (CLOS-1117 · yetim uç F).
 *
 * ### ⚠ Gövde ŞEMADAN: `clientWorkspaceProfileUpdateSchema` tek alan (`name`)
 *
 * Sunucu `strictObject`: fazla alan 422 (sahiplik alanları — unvan, vergi no — sponsorun
 * kanonik müvekkil kaydında). Form aynı şemayla doğruluyor; "Alan adı zorunludur" cümlesi
 * şemadan geliyor, ekran metin uydurmuyor (kriter 4). Sunucudan alan hatası dönerse
 * `applyServerErrors` forma işliyor.
 *
 * ### ⚠ Başarıda ÜÇ sorgu tazeleniyor
 *
 * Profil kartı (`workspace/profil`), üst çubuktaki alan adı (`["memberships"]` —
 * `/api/tenants/mine`) ve aktif alan bağlamı (`context`): ad üçünde de görünüyor; yalnız
 * profili tazeleseydik kullanıcı yeni adı kartta, eski adı üst çubukta görürdü.
 */
function AlanAdiDialog({
  open,
  mevcutAd,
  tenantId,
  onClose,
}: {
  open: boolean;
  mevcutAd: string;
  tenantId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const form = useZodForm(clientWorkspaceProfileUpdateSchema);

  /**
   * Pencere AÇILDIĞINDA mevcut adla kuruluyor.
   *
   * ⚠ Bağımlılık YALNIZ `open` (`ContractBasicsDialog` dersi): profil arka planda tazelenince
   * kullanıcının yazdığı taslak sunucu değeriyle sessizce ezilmesin.
   */
  useEffect(() => {
    if (open) form.reset({ name: mevcutAd });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- taslak yalnız AÇILIŞTA beslenir
  }, [open]);

  const kaydet = useMutation({
    mutationFn: (girdi: { name: string }) =>
      api<ClientWorkspaceProfile>("/api/workspace/profile", {
        method: "PATCH",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "workspace", "profil") }),
        queryClient.invalidateQueries({ queryKey: ["memberships"] }),
        queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "context") }),
      ]);
      toast.success("Alan adı güncellendi");
      onClose();
    },
    onError: (hata) => {
      if (hata instanceof ApiRequestError && hata.fields) {
        applyServerErrors(form, hata);
        return;
      }
      toast.error(hata instanceof ApiRequestError ? hata.message : "Alan adı güncellenemedi");
    },
  });

  const gonder = form.handleSubmit((girdi) => kaydet.mutate(girdi));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Alan adını düzenle"
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button type="button" onClick={() => void gonder()} loading={kaydet.isPending}>
            Kaydet
          </Button>
        </>
      }
    >
      <form
        onSubmit={(olay) => {
          olay.preventDefault();
          void gonder();
        }}
      >
        <FormField label="Alan adı" required error={form.formState.errors.name?.message}>
          <Input {...form.register("name")} />
        </FormField>
      </form>
    </Modal>
  );
}
